import { TabsProps } from 'react-bootstrap';
import BTabs from 'react-bootstrap/Tab';
import BNav from 'react-bootstrap/Nav';
import * as React from 'react';

type ContainerProps<TKeys extends string> = React.PropsWithChildren &
  Pick<TabsProps, 'id'> & {
    defaultActiveKey?: TKeys;
    activeKey?: TKeys;
    onSelect?: (
      eventKey: TKeys | undefined,
      e: React.SyntheticEvent<unknown>,
    ) => void;
  };

function Container<TKeys extends string>({
  children,
  ...props
}: ContainerProps<TKeys>) {
  return (
    <BTabs.Container
      {...props}
      onSelect={(key, e) => {
        props.onSelect?.((key ?? undefined) as TKeys | undefined, e);
      }}
    >
      {children}
    </BTabs.Container>
  );
}

type ListProps = React.PropsWithChildren;
function List(props: ListProps) {
  return <BNav variant='tabs'>{props.children}</BNav>;
}

type ItemProps<TKeys extends string> = React.PropsWithChildren &
  Pick<React.ComponentProps<typeof BNav.Link>, 'disabled'> & {
    eventKey: TKeys;
    /**
     * Required because it will be used as Data-cy
     */
    id: string;
  };

function Item<TKeys extends string>(props: ItemProps<TKeys>) {
  return (
    <BNav.Item>
      <BNav.Link {...props} data-cy={props.id}>
        {props.children}
      </BNav.Link>
    </BNav.Item>
  );
}

type ContentProps = React.PropsWithChildren;
function Content({ children, ...props }: ContentProps) {
  return <BTabs.Content {...props}>{children}</BTabs.Content>;
}

type PaneProps<TKeys extends string> = React.PropsWithChildren & {
  eventKey: TKeys;
};

function Pane<TKeys extends string>({ children, ...props }: PaneProps<TKeys>) {
  return <BTabs.Pane {...props}>{children}</BTabs.Pane>;
}

function TabsFactory<TKeys extends string>() {
  return {
    Container(props: ContainerProps<TKeys>) {
      return <Container {...props} />;
    },
    List(props: ListProps) {
      return <List {...props} />;
    },
    Item(props: ItemProps<TKeys>) {
      return <Item {...props} />;
    },
    Content(props: ContentProps) {
      return <Content {...props} />;
    },
    Pane(props: PaneProps<TKeys>) {
      return <Pane {...props} />;
    },
    isOpened: (current: TKeys | undefined, ...args: Array<TKeys>) =>
      args.some((item) => item === current),
  };
}
export default TabsFactory;
